import React, { useState, useRef, useEffect } from "react";
import "./FilterRangeSelector.scss";
import { CaretLeftIcon, CaretRightIcon } from "ui-component/custom-icons/icons";
import useIsMobile from 'utils/isMobileUtils';
import ReactSelect from 'react-select';
import { FaTimes } from 'react-icons/fa';

const years = [2025, 2026];
const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const quarters = ["Q1","Q2","Q3","Q4"];

// ✅ Clear
export default function FilterRangeSelector({
    showFilter,
    defaultSelectedStartYear,
    defaultSelectedEndYear,
    defaultSelectedStartMonth,
    defaultSelectedEndMonth,
    onFilterVisibility,
    showFilterBy,
    onApplyFilter
}) {
    const isMobile = useIsMobile();
    const monthWidth = 35;
    const totalMonths = years.length * 12;

    const [start, setStart] = useState(0);
    const [end, setEnd] = useState(12);

    const [dragging, setDragging] = useState(null);
    const compRef = useRef(null);

    const startX = useRef(0);
    const startStart = useRef(0);
    const startEnd = useRef(0);

    const [leftRangeYear, setLeftRangeYear] = useState(defaultSelectedStartYear || 2025);
    const [rightRangeYear, setRightRangeYear] = useState(defaultSelectedEndYear || 2026);

    const [selectedShowBy, setSelectedShowBy] = useState(showFilterBy || 'MONTH');


    const manualShowFilterByOptions = [
        { label: 'MONTH', value: 'MONTH' },
        { label: 'QUARTER', value: 'QUARTER' },
        { label: 'YEAR', value: 'YEAR' }
    ];

    const manualShowMonthsOptions = [
        { label: 'January', value: 1 },
        { label: 'February', value: 2 },
        { label: 'March', value: 3 },
        { label: 'April', value: 4 },
        { label: 'May', value: 5 },
        { label: 'June', value: 6 },
        { label: 'July', value: 7 },
        { label: 'August', value: 8 },
        { label: 'September', value: 9 },
        { label: 'October', value: 10 },
        { label: 'November', value: 11 },
        { label: 'December', value: 12 }
    ]

    const manualShowYearsOptions = [
        { label: '2026', value: 2026 },
        { label: '2025', value: 2025 },
        { label: '2024', value: 2024 },
        { label: '2023', value: 2023 },
        { label: '2022', value: 2022 },
        { label: '2021', value: 2021 },
        { label: '2020', value: 2020 },
    ]

    const [mobileFilterValues, setMobileFilterValues] = useState({
        showBy: showFilterBy || 'MONTH',
        from: {
            year: defaultSelectedStartYear || 2025,
            month: defaultSelectedStartMonth || 1,
        },
        to: {
            year: defaultSelectedEndYear || 2026,
            month: defaultSelectedEndMonth || 12,
        },
    });


    /* ------------------------------------------------------------------
       🎯 DEFAULT RANGE = CURRENT YEAR
    ------------------------------------------------------------------ */

    useEffect(() => {
        if (defaultSelectedStartYear && defaultSelectedEndYear && defaultSelectedStartMonth && defaultSelectedEndMonth) {
            const startYearIndex = years.indexOf(defaultSelectedStartYear);
            const endYearIndex = years.indexOf(defaultSelectedEndYear);
            if (startYearIndex !== -1 && endYearIndex !== -1) {
                const newStart = (startYearIndex * 12) + (defaultSelectedStartMonth) - 1;
                // const newStart = (startYearIndex * 12);
                const newEnd = (endYearIndex * 12) + (defaultSelectedEndMonth);

                setStart(newStart);
                setEnd(newEnd);
                const leftYear = defaultSelectedStartYear === defaultSelectedEndYear ? defaultSelectedStartYear - 1 : defaultSelectedStartYear;
                setLeftRangeYear(leftYear);
                setRightRangeYear(defaultSelectedEndYear);
            }
        }
    }, [])

	const handleSubmitMobileFilter = () => {


        onApplyFilter(
            mobileFilterValues.from.year,
            mobileFilterValues.to.year,
            mobileFilterValues.from.month,
            mobileFilterValues.to.month,
            mobileFilterValues.showBy
        );
    };
    
    const getSelectedMonths = (startIndex, endIndex) => {
    const selected = [];
    const safeStart = Math.max(0, startIndex);
    const safeEnd = Math.min(endIndex, totalMonths - 1); // clamp to last valid month
    const difference = safeEnd - safeStart;

    let firstMonth = null;
    let lastMonth = null;

    for (let i = safeStart; i <= safeEnd; i++) {
        const yearIndex = Math.floor(i / 12);
        const monthIndex = i % 12;
        const month = String(monthIndex + 1).padStart(2, "0");

        if (firstMonth === null) firstMonth = month;
        lastMonth = month;

        selected.push({
            year: years[yearIndex],
            month,
            monthIndex
        });
    }

    return selected;
};


const getMonthFromIndex = (index) => {
  return (index % 12) + 1;
};
    
    /* ------------------------------------------------------------------
       🖱 Drag Logic
    ------------------------------------------------------------------ */
    const handleDown = (type, e) => {
        e.preventDefault();
        setDragging(type);
        startX.current = e.clientX;
        startStart.current = start;
        startEnd.current = end;
    };

    const handleMove = (e) => {
        if (!dragging) return;

        const dx = e.clientX - startX.current;
        const delta = Math.round(dx / monthWidth);

        if (dragging === "left") {
            const newStart = Math.min(startEnd.current - 1, Math.max(0, startStart.current + delta));
            setStart(newStart);
        }

        if (dragging === "right") {
            const newEnd = Math.max(startStart.current + 1, Math.min(totalMonths, startEnd.current + delta));
            setEnd(newEnd);
        }

        if (dragging === "move") {
            let ns = startStart.current + delta;
            let ne = startEnd.current + delta;

            if (ns < 0 && ne < 25) {
                ne += -ns;
                ns = 0;
            }
                
            if (ne > totalMonths) {
                ns -= (ne - (totalMonths));
                ne = totalMonths;
            }
            setStart(ns);
            setEnd(ne);
        }
    };

    const handleUp = () => setDragging(null);

    /* ------------------------------------------------------------------
       ❌ Clicking outside closes popup
    ------------------------------------------------------------------ */
    const handleClickOutside = (e) => {
        if (compRef.current && !compRef.current.contains(e.target)) {
            onFilterVisibility(false);
        }
    };

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);


    /* ------------------------------------------------------------------
         ✅ PROCESS FILTER VALUES
     ------------------------------------------------------------------ */

    const processFilterValues = () => {
        const rangeValues = getRangeValues();
        
        onApplyFilter(
            rangeValues.from.year,
            rangeValues.to.year,
            rangeValues.from.month,
            rangeValues.to.month,
            rangeValues.showBy
        );
        onFilterVisibility(false);
    };
    /* ---------------------------------------------------------
        FORMAT OUTPUT
    ---------------------------------------------------------- */
    
    const getRangeValues = () => {
        const fromIndex = start;
        const toIndex = end - 1; // end is exclusive
        return {
            showBy: selectedShowBy,
            from: {
                // year: leftRangeYear,
                year: fromIndex >= 12 ? rightRangeYear : leftRangeYear,
                month: getMonthFromIndex(fromIndex),
            },
            to: {
                year: toIndex >= 12 ? rightRangeYear : leftRangeYear,
                month: getMonthFromIndex(toIndex),
            },
        };
    };
    if (isMobile) {
        return (
            <>
                
                {showFilter && <div className="filter-bg">&nbsp;</div>}
				{showFilter && (
					<div className="new-filter-container">
						<div className="filter-menu" style={{ background: '#fff', borderRadius: 12 }}>
					
							<div
								className="filter-content"
								style={{
                                    padding:  14,
								}}
                            >
								<div className="filter-children">
									
                                    <div className="filter-item" style={{ marginBottom: '10px' }}>
                                        <label htmlFor="">Show Per</label>
                                        <ReactSelect
                                            isMulti={false}
                                            className="rs-filter-container"
                                            classNamePrefix="rs-filter"
                                            options={manualShowFilterByOptions}
                                            onChange={(e) => {
                                                setMobileFilterValues((prev) => ({...prev, showBy: e.value}));
                                            }}
                                            value={manualShowFilterByOptions.find(option => option.value === mobileFilterValues.showBy)}
                                            defaultValue={'MONTH'}
                                            placeholder="Select or type..."
                                        />
                                    </div>
                                    <div className="filter-item" style={{ marginBottom: '10px' }}>
                                        <label htmlFor="">Start Month </label>
                                        <ReactSelect
                                            isMulti={false}
                                            className="rs-filter-container"
                                            classNamePrefix="rs-filter"
                                            options={manualShowMonthsOptions}
                                            onChange={(e) => {
                                                setMobileFilterValues((prev) => ({...prev, from: {...prev.from, month: e.value}}));
                                            }}
                                            value={manualShowMonthsOptions.find(option => option.value === mobileFilterValues.from.month)}
                                            placeholder="Select or type..."
                                        />
                                    </div>
                                    <div className="filter-item" style={{ marginBottom: '10px' }}>
                                        <label htmlFor="">Start Year </label>
                                        <ReactSelect
                                            isMulti={false}
                                            className="rs-filter-container"
                                            classNamePrefix="rs-filter"
                                            options={manualShowYearsOptions}
                                            onChange={(e) => {
                                                setMobileFilterValues((prev) => ({...prev, from: {...prev.from, year: e.value}}));
                                            }}
                                            value={manualShowYearsOptions.find(option => option.value === mobileFilterValues.from.year)}
                                            placeholder="Select or type..."
                                        />
                                    </div>
                                    <div className="filter-item" style={{ marginBottom: '10px' }}>
                                        <label htmlFor="">End Month </label>
                                        <ReactSelect
                                            isMulti={false}
                                            className="rs-filter-container"
                                            classNamePrefix="rs-filter"
                                            options={manualShowMonthsOptions}
                                            onChange={(e) => {
                                                setMobileFilterValues((prev) => ({...prev, to: {...prev.to, month: e.value}}));
                                            }}
                                            value={manualShowMonthsOptions.find(option => option.value === mobileFilterValues.to.month)}
                                            placeholder="Select or type..."
                                        />
                                    </div>
                                    <div className="filter-item" style={{ marginBottom: '10px' }}>
                                        <label htmlFor="">End Year </label>
                                        <ReactSelect
                                            isMulti={false}
                                            className="rs-filter-container"
                                            classNamePrefix="rs-filter"
                                            options={manualShowYearsOptions}
                                            onChange={(e) => {
                                                setMobileFilterValues((prev) => ({...prev, to: {...prev.to, year: e.value}}));
                                            }}
                                            value={manualShowYearsOptions.find(option => option.value === mobileFilterValues.to.year)}
                                            placeholder="Select or type..."
                                        />
                                    </div>
								</div>
							</div>
							<div
								className="filter-submit"
								style={{
									width: '100%',
									background: '#fff',
									zIndex: 100,
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
									gap: 12,
									padding: '16px 0 12px 0'
								}}
							>
								<button
									className="btn btn-main"
									style={{ minWidth: 'unset' }}
                                    onClick={handleSubmitMobileFilter}
                                    type="button"
								>
									Submit
								</button>
                                <button
                                    className="btn new-btn-error"
                                    onClick={() => onFilterVisibility(false) }
                                >
									<FaTimes /> Close
								</button>
							</div>
						</div>
					</div>
				)}
            </>
        )
    } else {

        return (
            <>
                {showFilter && (
                    <div className="filter-bg" onClick={() => onFilterVisibility(false)} />
                )}
    
                <div className="filter-range-popup-container" ref={compRef}>
                    <div
                        className="popup-body"
                        onMouseMove={handleMove}
                        onMouseUp={handleUp}
                        onMouseLeave={handleUp}
                    >
    
                        {/* TIMELINE WRAPPER */}
                        <div className="timeline-wrapper">
                            <div className="year-blocks">
    
    
                                <div className="year-section">

                                    {/* MONTHS */}
                                    <div className="months-row">
                                        {months.map((m, i) => (
                                            <div
                                                key={i}
                                                className="month-item"
                                                style={{ width: monthWidth }}
                                            >
                                                {m}
                                            </div>
                                        ))}
                                    </div>

                                    {/* QUARTERS */}
                                    <div className="quarters-row">
                                        {quarters.map((q, i) => (
                                            <div
                                                key={i}
                                                className="quarter-item"
                                                style={{ width: monthWidth * 3 }}
                                            >
                                                {q}
                                            </div>
                                        ))}
                                    </div>

                                    {/* YEAR LABEL */}
                                    <div className="year-label">
                                        <button
                                            onClick={() => {
                                                setLeftRangeYear(leftRangeYear - 1);
                                            }}
                                        >
                                            <CaretLeftIcon width="30" height="30" />
                                        </button>

                                        <span>{leftRangeYear}</span>

                                        <button
                                            className={`${leftRangeYear + 1 < rightRangeYear ? '' : 'disabled'}`}
                                            onClick={() => {
                                                const newLeftRangeYear = leftRangeYear + 1;
                                                if (newLeftRangeYear < rightRangeYear) {
                                                    setLeftRangeYear(leftRangeYear + 1);
                                                }
                                            }}
                                        >
                                            <CaretRightIcon width="30" height="30" />
                                        </button>
                                    </div>
                                </div>
    
    
    
                                <div className="year-section">

                                    {/* MONTHS */}
                                    <div className="months-row">
                                        {months.map((m, i) => (
                                            <div
                                                key={i}
                                                className="month-item"
                                                style={{ width: monthWidth }}
                                            >
                                                {m}
                                            </div>
                                        ))}
                                    </div>

                                    {/* QUARTERS */}
                                    <div className="quarters-row">
                                        {quarters.map((q, i) => (
                                            <div
                                                key={i}
                                                className="quarter-item"
                                                style={{ width: monthWidth * 3 }}
                                            >
                                                {q}
                                            </div>
                                        ))}
                                    </div>

                                    {/* YEAR LABEL */}
                                    <div className="year-label">
                                        <button
                                            className={`${rightRangeYear - 1 > leftRangeYear ? '' : 'disabled'}`}
                                            onClick={() => {
                                                const newRightRangeYear = rightRangeYear - 1;
                                                if (newRightRangeYear > leftRangeYear) {
                                                    setRightRangeYear(rightRangeYear - 1);
                                                }
                                            }}
                                        >
                                            <CaretLeftIcon width="30" height="30" />
                                        </button>

                                        <span>{rightRangeYear}</span>

                                        <button
                                            onClick={() => {
                                                setRightRangeYear(rightRangeYear + 1);
                                            }}
                                        >
                                            <CaretRightIcon width="30" height="30" />
                                        </button>
                                    </div>
                                </div>
                            </div>
    
                            {/* GREEN RANGE BOX */}
                            <div
                                className="range-box"
                                style={{
                                    left: start * monthWidth,
                                    width: ((end - start + 1) * monthWidth) - 15
                                }}
                            >
                                <div className="handle left" onMouseDown={(e) => handleDown("left", e)} />
                                <div className="move-area" onMouseDown={(e) => handleDown("move", e)} />
                                <div className="handle right" onMouseDown={(e) => handleDown("right", e)} />
                            </div>
                        </div>
    
                        {/* SHOW PER + FILTER BUTTON */}
                        <div className="row" style={{ marginTop: "25px" }}>
                            <div className="col-12" style={{ fontSize: '14px' }}>
                                Show Per
                            </div>
                        </div>
                        <div className="view-buttons">
                            <div className="flex" style={{ gap: "5px" }}>
                                <button 
                                    className={`${selectedShowBy === 'MONTH' ? 'active' : ''}`}
                                    onClick={() => {
                                        setSelectedShowBy('MONTH');
                                    }}
                                >
                                    Month
                                </button>
                                <button
                                    className={`${selectedShowBy === 'QUARTER' ? 'active' : ''}`}
                                    onClick={() => {
                                        setSelectedShowBy('QUARTER');
                                    }}
                                >
                                    Quarter
                                </button>
                                <button
                                    className={`${selectedShowBy === 'YEAR' ? 'active' : ''}`}
                                    onClick={() => {
                                        setSelectedShowBy('YEAR');
                                    }}
                                >
                                    Year
                                </button>
                            </div>
                            <button
                                className="filter-btn"
                                onClick={() => {
                                    processFilterValues()
                                }}
                            >
                                Filter
                            </button>
                        </div>
                    </div>
                </div>
            </>
        );
    }
}
